import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    IconButton,
    CircularProgress,
    Snackbar,
    FormHelperText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Event as EventIcon } from '@mui/icons-material';
import axios from 'axios';
import { workshopDomain } from '../../util/domain';
const EventGroupManagement = () => {
    const navigate = useNavigate();
    // State variables
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [currentGroupId, setCurrentGroupId] = useState(null);
    const [availableEvents, setAvailableEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    // Fetch all event groups
    const fetchGroups = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.get(`${workshopDomain}/api/event-groups`, config);
            if (response.data.success) {
                setGroups(response.data.groups);
            }
        } catch (error) {
            setError('Failed to fetch event groups');
            handleShowNotification('Failed to fetch event groups', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch all events
    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.get(`${workshopDomain}/api/events`, config);
            if (response.data.success) {
                setAvailableEvents(response.data.events);
            }
        } catch (error) {
            setError('Failed to fetch events');
            handleShowNotification('Failed to fetch events', 'error');
        }
    };

    // Fetch events for a specific group
    const fetchGroupEvents = async (groupId) => {
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.get(`${workshopDomain}/api/event-groups/${groupId}`, config);
            if (response.data.success) {
                setCurrentGroup({
                    ...response.data.group,
                    events: response.data.events
                });
            }
        } catch (error) {
            setError('Failed to fetch group events');
            handleShowNotification('Failed to fetch group events', 'error');
        }
    };

    // Load data on component mount
    // Check user role and authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');

        // Check if user has Admin role
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');

        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchGroups();
        fetchEvents();
    }, [navigate]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Open dialog for creating a new group
    const handleCreateGroup = () => {
        setFormData({
            name: '',
            description: ''
        });
        setEditMode(false);
        setOpen(true);
    };

    // Open dialog for editing a group
    const handleEditGroup = (group) => {
        setFormData({
            name: group.name,
            description: group.description || ''
        });
        setCurrentGroupId(group.group_id);
        setEditMode(true);
        setOpen(true);
    };

    // Open dialog for managing events in a group
    const handleManageEvents = async (groupId) => {
        setCurrentGroupId(groupId);
        await fetchGroupEvents(groupId);

        // Set initially selected events
        const groupEvents = currentGroup?.events || [];
        setSelectedEvents(groupEvents.map(event => event.hash_id));

        setEventsDialogOpen(true);
    };

    // Open confirm delete dialog
    const handleOpenDeleteDialog = (groupId) => {
        setCurrentGroupId(groupId);
        setConfirmDeleteDialogOpen(true);
    };

    // Handle event selection changes
    const handleEventSelectionChange = (event) => {
        setSelectedEvents(event.target.value);
    };

    // Close dialogs
    const handleCloseDialog = () => {
        setOpen(false);
        setEventsDialogOpen(false);
        setConfirmDeleteDialogOpen(false);
        setCurrentGroupId(null);
        setSelectedEvents([]);
    };

    // Show notification
    const handleShowNotification = (message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            open: false
        });
    };

    // Submit form to create or update group
    const handleSubmit = async () => {
        if (!formData.name) {
            setError('Group name is required');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            let response;
            if (editMode) {
                // Update existing group
                response = await axios.put(
                    `${workshopDomain}/api/event-groups/${currentGroupId}`,
                    formData,
                    config
                );
                if (response.data.success) {
                    handleShowNotification('Event group updated successfully');
                }
            } else {
                // Create new group
                response = await axios.post(
                    `${workshopDomain}/api/event-groups`,
                    formData,
                    config
                );
                if (response.data.success) {
                    handleShowNotification('Event group created successfully');
                }
            }

            fetchGroups();
            handleCloseDialog();
        } catch (error) {
            setError('Failed to save event group');
            handleShowNotification('Failed to save event group', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Save event selections for a group
    const handleSaveEvents = async () => {
        if (!currentGroupId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            const response = await axios.post(`${workshopDomain}/api/event-groups/${currentGroupId}/events`, {
                eventIds: selectedEvents
            }, config);

            if (response.data.success) {
                handleShowNotification('Events updated successfully');
                fetchGroups();
            }

            handleCloseDialog();
        } catch (error) {
            setError('Failed to update events');
            handleShowNotification('Failed to update events', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete a group
    const handleDeleteGroup = async () => {
        if (!currentGroupId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await axios.delete(
                `${workshopDomain}/api/event-groups/${currentGroupId}`,
                config
            );

            if (response.data.success) {
                handleShowNotification('Event group deleted successfully');
                fetchGroups();
            }

            handleCloseDialog();
        } catch (error) {
            setError('Failed to delete event group');
            handleShowNotification('Failed to delete event group', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Event Group Management</Typography>
                <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    color="primary"
                    onClick={handleCreateGroup}
                >
                    Create New Group
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !groups.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {groups.map((group) => (
                        <Grid item key={group.group_id} xs={12} md={6} lg={4}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" gutterBottom>
                                        {group.name}
                                    </Typography>
                                    <Chip
                                        color={group.is_active ? "success" : "default"}
                                        size="small"
                                        label={group.is_active ? 'Active' : 'Inactive'}
                                        sx={{ mb: 2 }}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {group.description || 'No description provided'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Group ID:</strong> {group.group_id}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Events:</strong> {group.event_count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Created:</strong> {new Date(group.created_at).toLocaleDateString()}
                                    </Typography>
                                    <Link href={`https://workshop.vectorindia.org/groupedevent/${group.group_id}`} target="_blank" rel="noopener noreferrer">
                                        {`https://workshop.vectorindia.org/groupedevent/${group.group_id}`}
                                    </Link>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        startIcon={<EventIcon />}
                                        onClick={() => handleManageEvents(group.group_id)}
                                    >
                                        Manage Events
                                    </Button>
                                    <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditGroup(group)}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleOpenDeleteDialog(group.group_id)}
                                    >
                                        Delete
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}

                    {groups.length === 0 && !loading && (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', my: 4 }}>
                                <Typography variant="body1">
                                    No event groups found. Create your first group!
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Create/Edit Group Dialog */}
            <Dialog
                open={open}
                onClose={handleCloseDialog}
            >
                <DialogTitle>
                    {editMode ? 'Edit Event Group' : 'Create New Event Group'}
                </DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 2,
                            width: 400,
                            maxWidth: '100%'
                        }}
                    >
                        <TextField
                            required
                            fullWidth
                            label="Group Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter group name"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter group description (optional)"
                            multiline
                            rows={3}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading || !formData.name}
                    >
                        {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Events Dialog */}
            <Dialog
                open={eventsDialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Manage Events for Group
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="events-select-label">Select Events</InputLabel>
                            <Select
                                labelId="events-select-label"
                                multiple
                                value={selectedEvents}
                                onChange={handleEventSelectionChange}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.length} event(s) selected
                                    </Box>
                                )}
                                sx={{ maxHeight: 300 }}
                            >
                                {availableEvents.map((event) => (
                                    <MenuItem key={event.hash_id} value={event.hash_id}>
                                        <FormControlLabel
                                            control={
                                                <Switch checked={selectedEvents.indexOf(event.hash_id) > -1} />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body1">{event.title}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {new Date(event.event_start_date).toLocaleDateString()} - {event.event_type}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ width: '100%', m: 0 }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveEvents}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Save Events'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete Dialog */}
            <Dialog
                open={confirmDeleteDialogOpen}
                onClose={handleCloseDialog}
            >
                <DialogTitle>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this event group? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteGroup}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EventGroupManagement; 